import React, { useEffect, useState, useContext, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AppContext } from './AppContext';
import { ShoppingCart, Heart, Trash2, Plus, Minus, X, Search, Star, ShoppingBag } from 'lucide-react';
import './Navbar.css';

const Navbar = () => {
  const { cart, wishlist, products, gifts, addToCart, removeFromCart, toggleWishlist, updateCartQuantity, t, lang, toggleLanguage } = useContext(AppContext);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalQty, setModalQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');

  const isMr = lang === 'mr';

  const resolveImg = (src) => {
    if (!src) return '/images/logo.png';
    if (typeof src !== 'string') return '/images/logo.png';
    const trimmed = src.trim();
    if (!trimmed) return '/images/logo.png';

    // 1. Data URLs (base64) & Blob URLs
    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
      return trimmed;
    }

    // 2. Absolute HTTP/HTTPS URLs
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }

    // 3. Already absolute path
    if (trimmed.startsWith('/')) {
      return trimmed;
    }

    // 4. Starts with images/ or Images/
    if (trimmed.toLowerCase().startsWith('images/')) {
      return `/${trimmed}`;
    }

    // 5. Raw filename like "men chain11.jpg" or "painjan.jpg"
    return `/images/${trimmed}`;
  };

  const handleProductImgError = (e, product) => {
    const imgEl = e.currentTarget;
    const step = Number(imgEl.dataset.fallbackStep || '0');

    // Step 0: Try without spaces in /images/
    if (step === 0 && product?.image && typeof product.image === 'string') {
      imgEl.dataset.fallbackStep = '1';
      const cleanImg = product.image.replace(/^(\/?images\/)/i, '').replace(/\s+/g, '');
      imgEl.src = `/images/${cleanImg}`;
      return;
    }

    // Step 1: Try matched keywords from product name if applicable
    if (step <= 1 && product?.name) {
      imgEl.dataset.fallbackStep = '2';
      const lowerName = product.name.toLowerCase();
      const matchNumber = lowerName.match(/\d+/);
      const num = matchNumber ? matchNumber[0] : '';
      if (lowerName.includes('men') && lowerName.includes('chain')) {
        imgEl.src = `/images/men chain${num || '10'}.jpg`;
        return;
      }
      if (lowerName.includes('chain')) {
        imgEl.src = `/images/w chain${num || '1'}.jpg`;
        return;
      }
      if (lowerName.includes('ring')) {
        imgEl.src = lowerName.includes('men') ? `/images/men ring${num || '2'}.jpg` : `/images/w ring${num || '1'}.jpg`;
        return;
      }
      if (lowerName.includes('payal') || lowerName.includes('painjan')) {
        imgEl.src = `/images/painjan${num || '1'}.jpg`;
        return;
      }
      if (lowerName.includes('bracelet') || lowerName.includes('kada')) {
        imgEl.src = lowerName.includes('men') ? `/images/men br${num || '6'}.jpg` : `/images/w br${num || '1'}.jpg`;
        return;
      }
    }

    // Final fallback to logo
    imgEl.src = '/images/logo.png';
  };

  const getLocalizedContent = (product, field) => {
    if (lang === 'mr' && product[`${field}Mr`]) {
      return product[`${field}Mr`];
    }
    return product[field];
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setModalQty(1);
    setSelectedSize(product.ringSize || 'Standard');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus();
  }, [searchOpen]);

  // Close search on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchOpen && !e.target.closest('.search-bar-wrap')) {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [searchOpen]);

  const totalCartItems = cart.reduce((total, item) => total + item.quantity, 0);
  const totalCartPrice = cart.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  const favoritedProducts = products.filter(p => wishlist.includes(p.id));

  // Search results
  const allItems = [...products, ...(gifts || [])];
  const searchResults = searchQuery.trim().length >= 2
    ? allItems.filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.category && p.category.toLowerCase().includes(searchQuery.toLowerCase()))
    ).slice(0, 6)
    : [];

  const handleSearchSelect = (item) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(`/products?category=${encodeURIComponent(item.category || item.subCategory || '')}`);
  };

  const isHomePage = location.pathname === '/';
  const navClass = `navbar ${scrolled ? 'scrolled' : ''} ${!isHomePage ? 'navbar-solid' : ''}`;

  return (
    <>
      <nav className={navClass}>
        <div className="navbar-inner">
          <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
            <span className={menuOpen ? 'open' : ''}></span>
            <span className={menuOpen ? 'open' : ''}></span>
            <span className={menuOpen ? 'open' : ''}></span>
          </button>

          <div className="navbar-logo" onClick={() => navigate('/')}>
            <img src="images/logo.png" alt="GDR Jewellers Logo" className="nav-logo-img" />
            {/* <div className="logo-text-group">
              <span className="logo-gdr">GDR</span>
              <span className="logo-sub">Jewellers</span>
            </div> */}
          </div>

          <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <li><Link to="/about" onClick={() => setMenuOpen(false)}>{t.aboutUs}</Link></li>
            <li><Link to="/products" onClick={() => setMenuOpen(false)}>{t.products}</Link></li>
            <li><Link to="/gifting" onClick={() => setMenuOpen(false)}>{t.giftsByOccasion}</Link></li>
            <li><Link to="/blog" onClick={() => setMenuOpen(false)}>{t.blog}</Link></li>
            <li><Link to="/contact" onClick={() => setMenuOpen(false)}>{t.contactUs}</Link></li>


            <div className="mobile-menu-actions">

              {/* SEARCH */}
              <div className={`search-bar-wrap mobile-search-wrap ${searchOpen ? 'open' : ''}`}>
                <button
                  className="mobile-menu-icon-btn"
                  onClick={() => {
                    setSearchOpen(s => !s);
                    setWishlistOpen(false);
                    setCartOpen(false);
                  }}
                  aria-label="Search"
                >
                  <Search size={21} />
                </button>

                {searchOpen && (
                  <div className="search-dropdown mobile-search-dropdown">
                    <input
                      ref={searchRef}
                      type="text"
                      placeholder={t.searchPlaceholder}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="search-input"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && searchResults.length > 0) {
                          handleSearchSelect(searchResults[0]);
                        }
                      }}
                    />

                    {searchResults.length > 0 && (
                      <div className="search-results">
                        {searchResults.map(r => (
                          <div
                            key={r.id}
                            className="search-result-item"
                            onClick={() => handleSearchSelect(r)}
                          >
                            <img src={r.image} alt={r.name} />

                            <div>
                              <span className="sr-name">{r.name}</span>
                              <span className="sr-price">
                                ₹{r.price.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {searchQuery.trim().length >= 2 &&
                      searchResults.length === 0 && (
                        <div className="search-no-results">
                          No results found
                        </div>
                      )}
                  </div>
                )}
              </div>


              {/* WISHLIST */}
              <button
                className="mobile-menu-icon-btn"
                onClick={() => {
                  setWishlistOpen(!wishlistOpen);
                  setCartOpen(false);
                  setSearchOpen(false);
                }}
                aria-label="Wishlist"
              >
                <Heart
                  size={22}
                  fill={wishlist.length > 0 ? '#b8860b' : 'none'}
                  color={wishlist.length > 0 ? '#b8860b' : 'currentColor'}
                />

                {wishlist.length > 0 && (
                  <span className="action-badge">
                    {wishlist.length}
                  </span>
                )}
              </button>


              {/* CART */}
              <button
                className="mobile-menu-icon-btn"
                onClick={() => {
                  setCartOpen(!cartOpen);
                  setWishlistOpen(false);
                  setSearchOpen(false);
                }}
                aria-label="Cart"
              >
                <ShoppingCart size={22} />

                {totalCartItems > 0 && (
                  <span className="action-badge">
                    {totalCartItems}
                  </span>
                )}
              </button>

            </div>

          </ul>

          <div className="navbar-actions">
            {/* Luxury Segmented Language Toggle Switch */}
            <div
              className={`lang-switch-toggle ${lang === 'mr' ? 'is-mr' : 'is-en'}`}
              role="group"
              aria-label="Language Selector"
              onClick={(e) => {
                if (e.target.closest('.lang-pill-btn')) return;
                toggleLanguage();
              }}
            >
              <div className="lang-switch-glider" />
              <button
                type="button"
                className={`lang-pill-btn ${lang === 'en' ? 'active' : ''}`}
                onClick={() => toggleLanguage('en')}
                aria-pressed={lang === 'en'}
                title="English"
              >
                <span className="desktop-lang-text">English</span>
                <span className="mobile-lang-short">EN</span>
              </button>
              <button
                type="button"
                className={`lang-pill-btn ${lang === 'mr' ? 'active' : ''}`}
                onClick={() => toggleLanguage('mr')}
                aria-pressed={lang === 'mr'}
                title="मराठी"
              >
                <span className="desktop-lang-text">मराठी</span>
                <span className="mobile-lang-short">मराठी</span>
              </button>
            </div>

            {/* Search bar */}
            <div className={`search-bar-wrap ${searchOpen ? 'open' : ''}`}>
              <button className="icon-action-btn" onClick={() => setSearchOpen(s => !s)}>
                <Search size={21} />
              </button>
              {searchOpen && (
                <div className="search-dropdown">
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder={t.searchPlaceholder}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="search-input"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && searchResults.length > 0) {
                        handleSearchSelect(searchResults[0]);
                      }
                    }}
                  />
                  {searchResults.length > 0 && (
                    <div className="search-results">
                      {searchResults.map(r => (
                        <div key={r.id} className="search-result-item" onClick={() => handleSearchSelect(r)}>
                          <img src={r.image} alt={r.name} />
                          <div>
                            <span className="sr-name">{r.name}</span>
                            <span className="sr-price">₹{r.price.toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                    <div className="search-no-results">No results found</div>
                  )}
                </div>
              )}
            </div>

            {/* Wishlist Button */}
            <button className="icon-action-btn" onClick={() => { setWishlistOpen(!wishlistOpen); setCartOpen(false); setSearchOpen(false); }}>
              <Heart size={22} fill={wishlist.length > 0 ? '#b8860b' : 'none'} color={wishlist.length > 0 ? '#b8860b' : 'currentColor'} />
              {wishlist.length > 0 && <span className="action-badge">{wishlist.length}</span>}
            </button>

            {/* Cart Button */}
            <button className="icon-action-btn" onClick={() => { setCartOpen(!cartOpen); setWishlistOpen(false); setSearchOpen(false); }}>
              <ShoppingCart size={22} />
              {totalCartItems > 0 && <span className="action-badge">{totalCartItems}</span>}
            </button>
          </div>
        </div>
      </nav>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="nav-drawer-overlay" onClick={() => setCartOpen(false)}>
          <div className="nav-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>{t.cartTitle} ({totalCartItems})</h3>
              <button className="btn-close-drawer" onClick={() => setCartOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="drawer-content">
              {cart.length === 0 ? (
                <div className="drawer-empty">
                  <ShoppingCart size={48} strokeWidth={1} />
                  <p>{t.emptyCart}</p>
                </div>
              ) : (
                <div className="drawer-items-list">
                  {cart.map((item, idx) => (
                    <div key={item.itemKey || idx} className="drawer-item" onClick={() => openProductModal(item.product)}>
                      <img
                        src={resolveImg(item.product.image)}
                        alt={item.product.name}
                        onError={(e) => handleProductImgError(e, item.product)}
                      />
                      <div className="drawer-item-info">
                        <h4>{getLocalizedContent(item.product, 'name')}</h4>
                        {item.selectedSize && <span className="drawer-item-size">Size: {item.selectedSize}</span>}
                        <span className="drawer-item-price">₹{item.product.price.toLocaleString()}</span>
                        <div className="drawer-item-qty">
                          <button onClick={(e) => { e.stopPropagation(); updateCartQuantity(item.itemKey || item.product.id, item.quantity - 1); }}><Minus size={12} /></button>
                          <span>{item.quantity}</span>
                          <button onClick={(e) => { e.stopPropagation(); updateCartQuantity(item.itemKey || item.product.id, item.quantity + 1); }}><Plus size={12} /></button>
                        </div>
                      </div>
                      <button className="drawer-item-remove" onClick={(e) => { e.stopPropagation(); removeFromCart(item.itemKey || item.product.id); }} title="Remove">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="drawer-footer">
                <div className="drawer-subtotal">
                  <span>{t.subtotal}:</span>
                  <strong>₹{totalCartPrice.toLocaleString()}</strong>
                </div>
                <button
                  className="btn-drawer-checkout"
                  onClick={() => {
                    setCartOpen(false);
                    const itemDetails = cart.map(i => `${i.product.name} (Qty: ${i.quantity}${i.selectedSize ? `, Size: ${i.selectedSize}` : ''})`).join('\n');
                    navigate(`/contact?subject=${encodeURIComponent('Cart Inquiry')}&details=${encodeURIComponent(itemDetails)}`);
                  }}
                >
                  {t.checkoutBtn}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Wishlist Drawer */}
      {wishlistOpen && (
        <div className="nav-drawer-overlay" onClick={() => setWishlistOpen(false)}>
          <div className="nav-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>{t.wishlistTitle} ({wishlist.length})</h3>
              <button className="btn-close-drawer" onClick={() => setWishlistOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="drawer-content">
              {favoritedProducts.length === 0 ? (
                <div className="drawer-empty">
                  <Heart size={48} strokeWidth={1} />
                  <p>{t.emptyWishlist}</p>
                </div>
              ) : (
                <div className="drawer-items-list">
                  {favoritedProducts.map(p => (
                    <div key={p.id} className="drawer-item" style={{ cursor: 'pointer' }} onClick={() => openProductModal(p)}>
                      <img
                        src={resolveImg(p.image)}
                        alt={p.name}
                        onError={(e) => handleProductImgError(e, p)}
                      />
                      <div className="drawer-item-info">
                        <h4>{getLocalizedContent(p, 'name')}</h4>
                        <span className="drawer-item-price">₹{p.price.toLocaleString()}</span>
                        <button
                          className="btn-wishlist-addcart"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(p, 1);
                            toggleWishlist(p.id);
                          }}
                        >
                          + {t.addToCart}
                        </button>
                      </div>
                      <button className="drawer-item-remove" onClick={(e) => { e.stopPropagation(); toggleWishlist(p.id); }} title="Remove">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ultra-Premium Product Detail Modal */}
      {selectedProduct && (
        <div className="nav-drawer-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="nav-product-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="nav-modal-close"
              onClick={() => setSelectedProduct(null)}
              aria-label="Close modal"
            >
              <X size={18} />
            </button>

            <div className="nav-modal-content">
              {/* Product Image Showcase with Hallmark Badge */}
              <div className="nav-modal-img-wrapper">
                <div className="nav-modal-img">
                  <img
                    src={resolveImg(selectedProduct.image)}
                    alt={selectedProduct.name}
                    onError={(e) => handleProductImgError(e, selectedProduct)}
                  />
                  <div className="nav-modal-badge">
                    <span>✦ BIS 925 HALLMARK ✦</span>
                  </div>
                </div>
              </div>

              {/* Product Details Column */}
              <div className="nav-modal-info">
                <div className="nav-modal-header-tag">
                  <span className="nav-modal-cat">{selectedProduct.category}</span>
                  {selectedProduct.subCategory && (
                    <span className="nav-modal-subtag">• {selectedProduct.subCategory}</span>
                  )}
                </div>

                <h3 className="nav-modal-title">{getLocalizedContent(selectedProduct, 'name')}</h3>

                <div className="nav-modal-rating-row">
                  <div className="stars-group">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star
                        key={s}
                        size={13}
                        fill={s <= Math.round(Number(selectedProduct.rating || 4.8)) ? '#D4AF37' : 'none'}
                        color="#D4AF37"
                      />
                    ))}
                  </div>
                  <span className="rating-score">{Number(selectedProduct.rating || 4.8).toFixed(1)} / 5.0</span>
                  <span className="rating-verified">✓ {isMr ? 'प्रमाणित शुद्धता' : 'Certified Purity'}</span>
                </div>

                <div className="nav-modal-price-box">
                  <div className="price-main">
                    <span className="currency-symbol">₹</span>
                    <span className="price-num">{Number(selectedProduct.price || 0).toLocaleString()}</span>
                  </div>
                  <span className="tax-inclusive">{isMr ? 'सर्व कर समाविष्ट' : 'Inclusive of all taxes'}</span>
                </div>

                {/* Product Specifications Grid */}
                <div className="nav-modal-spec-grid">
                  {selectedProduct.weight && selectedProduct.weight !== '0' && selectedProduct.weight !== 0 && (
                    <div className="nav-spec-card">
                      <span className="spec-label">{isMr ? 'अंदाजे वजन' : 'Approx. Weight'}</span>
                      <strong className="spec-value">{typeof selectedProduct.weight === 'number' || !String(selectedProduct.weight).toLowerCase().includes('g') ? `${selectedProduct.weight} g` : selectedProduct.weight}</strong>
                    </div>
                  )}

                  {selectedProduct.material && (
                    <div className="nav-spec-card">
                      <span className="spec-label">{isMr ? 'धातू' : 'Metal / Material'}</span>
                      <strong className="spec-value">{selectedProduct.material}</strong>
                    </div>
                  )}

                  <div className="nav-spec-card">
                    <span className="spec-label">{isMr ? 'शुद्धता हमी' : 'Purity Grade'}</span>
                    <strong className="spec-value gold-accent">92.5% Pure Silver</strong>
                  </div>
                </div>

                {selectedProduct.description && (
                  <p className="nav-modal-desc">{getLocalizedContent(selectedProduct, 'description')}</p>
                )}

                {/* Quantity & Premium Add to Cart Action Row */}
                <div className="nav-modal-action-row">
                  <div className="nav-qty-group">
                    <span className="qty-title">{t.quantity}:</span>
                    <div className="nav-qty-control">
                      <button
                        type="button"
                        onClick={() => setModalQty(q => Math.max(1, q - 1))}
                        aria-label="Decrease quantity"
                      >
                        <Minus size={13} />
                      </button>
                      <span>{modalQty}</span>
                      <button
                        type="button"
                        onClick={() => setModalQty(q => q + 1)}
                        aria-label="Increase quantity"
                      >
                        <Plus size={13} />
                      </button>
                    </div>
                  </div>

                  <button
                    className="nav-modal-add-btn"
                    onClick={() => {
                      addToCart(selectedProduct, modalQty, selectedSize);
                      setSelectedProduct(null);
                    }}
                  >
                    <ShoppingBag size={17} />
                    <span>{t.addToCart}</span>
                    <span className="btn-qty-badge">({modalQty})</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
